import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Stack, Typography, alpha,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { ClosedOutcome, Stage } from '../../api/types';
import { ApiError } from '../../api/client';
import { useThemeMode } from '../../theme/ThemeModeProvider';
import { stageColorKey } from './format';

/** Text that may hold RTL or mixed-direction user data inside LTR chrome. */
export function UserText({
  children, multiline = false, ...rest
}: { children: ReactNode; multiline?: boolean } & Record<string, unknown>) {
  return (
    <Box
      component="span"
      data-user-content=""
      dir="auto"
      sx={{ whiteSpace: multiline ? 'pre-wrap' : undefined, display: multiline ? 'block' : 'inline' }}
      {...rest}
    >
      {children}
    </Box>
  );
}

export function StageChip({ stage, outcome, size = 'small' }: {
  stage: Stage; outcome?: ClosedOutcome | null; size?: 'small' | 'medium';
}) {
  const { t } = useTranslation();
  const { stageColors } = useThemeMode();
  const color = stageColors[stageColorKey(stage, outcome ?? null)];
  const label = stage === 'closed' && outcome ? t(`outcome.${outcome}`) : t(`stage.${stage}`);

  return (
    <Chip
      size={size}
      label={label}
      data-testid={`stage-chip-${stage}`}
      sx={{
        color,
        backgroundColor: alpha(color, 0.14),
        border: `1px solid ${alpha(color, 0.35)}`,
      }}
    />
  );
}

export function LoadingState({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <Stack alignItems="center" spacing={2} sx={{ py: 8 }} data-testid="loading-state">
      <CircularProgress size={28} />
      <Typography variant="body2" color="text.secondary">{label ?? t('common.loading')}</Typography>
    </Stack>
  );
}

export function EmptyState({ title, description, action }: {
  title: string; description?: string; action?: ReactNode;
}) {
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ py: 8, px: 3, textAlign: 'center' }} data-testid="empty-state">
      <Box
        component="img"
        src="/brand/aiqbee-square.png"
        alt=""
        sx={{ width: 56, height: 56, opacity: 0.35, mb: 1 }}
      />
      <Typography variant="h3">{title}</Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
          {description}
        </Typography>
      )}
      {action && <Box sx={{ pt: 1 }}>{action}</Box>}
    </Stack>
  );
}

/** Known error codes get a translated message; anything else falls back to the server's English
 *  text, which is better than a generic sentence that hides what actually went wrong. Conflicts
 *  and field validation are deliberately absent from the map for that reason — "a partner with
 *  this name already exists" tells the user what to do, and "that conflicts with something"
 *  does not. */
export function useErrorMessage(): (error: unknown) => string {
  const { t } = useTranslation();
  return (error: unknown) => {
    if (error instanceof ApiError) {
      const key = `errors.${error.code}`;
      const translated = t(key);
      return translated === key ? error.message : translated;
    }
    return error instanceof Error ? error.message : t('errors.generic');
  };
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage()(error);
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ py: 8, px: 3, textAlign: 'center' }} data-testid="error-state">
      <ErrorOutlineIcon color="error" sx={{ fontSize: 40 }} />
      <Typography variant="h3">{t('errors.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>{message}</Typography>
      {onRetry && <Button variant="outlined" onClick={onRetry}>{t('common.tryAgain')}</Button>}
    </Stack>
  );
}

export function ConfirmDialog({
  open, title, message, confirmLabel, destructive = true, onConfirm, onClose, busy = false,
}: {
  open: boolean; title: string; message: string; confirmLabel?: string;
  destructive?: boolean; onConfirm: () => void; onClose: () => void; busy?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          disabled={busy}
          data-testid="confirm-button"
        >
          {confirmLabel ?? t('common.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
